// @ts-check
// datahooks/useMenuBootstrap.js

/** @typedef {import('./types/tagging.types').OrganisationTag} OrganisationTag */
/** @typedef {import('./types/tagging.types').ProjectTagValuesResponse} ProjectTagValuesResponse */
import { useCallback, useMemo } from 'react';

import useProjects from './useProjects';
import useOrganisationTags from './tagging/useOrganisationTags';
import useProjectTagValues from './admin/project/useProjectTagValues';

import { buildProjectTagIndex, applyProjectTags } from '@components/navigation/menuUtils';

/**
 * Hook to fetch projects and tags, including lookup data.
 * Combines loading and error states.
 *
 * Provides a refetchAll function to refresh all datasets.
 *
 * @returns {{
 *  projects: any[],
 *  orgTags: OrganisationTag[],
 *  projectTagValues: ProjectTagValuesResponse,
 *  projectsWithTags: any[], // projects with tag data applied for easier use in UI
 *  projectTagIndex: Map<number, Record<string, Array<{ id: number, title: string }>>>, // index for looking up tag values by tag slug
 *  isLoading: boolean,
 *  error: any,
 *  refetchAll: ()=> Promise<void>,
 * }}
 */
export default function useMenuBootstrap() {
  const { projects = [], isLoading: loadingProjects, error: projectsError, refetch: refetchProjects } = useProjects();
  const { orgTags = [], isLoading: loadingTags, error: tagsError, refetch: refetchTags } = useOrganisationTags();
  const { projectTagValues = [], isLoading: loadingTagValues, isPreviousData: tagValuesArePrevious = false, error: tagValuesError, refetch: refetchTagValues } = useProjectTagValues();

  const refetchAll = useCallback(async () => {
    // Memoised function to avoid re-renders
    await Promise.all([refetchProjects(), refetchTags(), refetchTagValues()]);
  }, [refetchProjects, refetchTags, refetchTagValues]);

  // The values keep the previous organisation's data while the next fetch runs, with isLoading false;
  // isPreviousData covers that window.
  const projectTagIndex = useMemo(
    () => (loadingTags || loadingTagValues || tagValuesArePrevious ? new Map() : buildProjectTagIndex(projectTagValues, orgTags)),
    [projectTagValues, orgTags, loadingTags, loadingTagValues, tagValuesArePrevious]
  );

  const projectsWithTags = useMemo(() => applyProjectTags(projects, projectTagIndex), [projects, projectTagIndex]); // Apply tag data to projects for easier consumption in the UI

  return {
    projects,
    orgTags, // lookups for grouping/filter UI
    projectTagValues,
    projectsWithTags, // projects with tag data applied for easier use in UI
    projectTagIndex, // index for looking up tag values by tag_id and tag_value_id
    isLoading: loadingProjects || loadingTags || loadingTagValues,
    error: projectsError || tagsError || tagValuesError || null,
    refetchAll,
  };
}